import { useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import { t, ta } from "../../../lib/sprache";
import { Link } from "react-router";
import { useStaffWorkspace } from "../../../app/session";
import { loadMusterZeilen, musterHinweisText, musterKennung, musterLandHinweis, type MusterZeile } from "./muster";

export { musterKennung, musterLandHinweis, musterZuVorlage, type MusterZeile } from "./muster";

/**
 * Vertragsmuster (Anwalt Punkt 13, Zusatz 24/25; 0181): CreatorOffice-Vorlagen sind unverbindliche Muster. Vorlagenauswahl, Galerie,
 * Vorlagen- und Vertragseditor zeigen den Hinweis und die Kennung „Kooperationsvertrag DE · Version 1 · Stand Oktober 2026“
 * (platform_contract_templates.version, jurisdiction, legal_as_of). Ein Muster nach deutschem Recht wird Konten in Österreich oder
 * der Schweiz nicht als passend dargestellt.
 */

/** Muster-Zeilen der Plattform (gecacht), Sitzland des Kontos */
export function useMuster() {
  const ws = useStaffWorkspace();
  const q = useQuery({ queryKey: ["platform_contract_templates", "muster"], queryFn: () => loadMusterZeilen(api), staleTime: 3_600_000 });
  return { zeilen: q.data, country: ws.org.country ?? null };
}

/**
 * Hinweis auf das unverbindliche Muster mit Kennung. Ohne Zeile (eigene Vorlage der Agentur) nur dann, wenn `immer` gesetzt ist
 * (Galerie, Vorlagenauswahl).
 */
export function MusterHinweis({ zeile, version, immer = false, kompakt = false }: { zeile?: MusterZeile | null; version?: number | null; immer?: boolean; kompakt?: boolean }) {
  const { country } = useMuster();
  if (!zeile && !immer) return null;
  const land = zeile ? musterLandHinweis(zeile.jurisdiction, country) : null;
  return (
    <div className={`notice${land ? " warning" : ""} stack tk-muster-hinweis`} role="note">
      {zeile && <strong>{musterKennung(zeile, version)}</strong>}
      <p className={kompakt ? "muted" : undefined}>{musterHinweisText()}</p>
      {land && <p>{land}</p>}
      {/* K-401/K-412: bearbeitete Kopie einer älteren Fassung (unbearbeitete Entwürfe hebt 0291 selbst an) */}
      {zeile && version != null && zeile.version > version && (
        <p><strong>{t(`Es gibt eine neuere Fassung dieses Musters (Version ${zeile.version}).`, `A newer version of this sample exists (version ${zeile.version}).`)}</strong>{" "}
          {t("Übernehmen lässt sie sich unter Verträge · Vorlagen in den CreatorOffice-Vorlagen mit „Neue Fassung übernehmen“; eigene Änderungen bitte danach erneut eintragen.", "You can take it over under Contracts · Templates in the CreatorOffice templates with “Take over new version”; please re-enter your own changes afterwards.")}{" "}
          <Link to="/agentur/vertraege/vorlagen">{t("Zu den Vorlagen", "Go to templates")}</Link></p>
      )}
    </div>
  );
}

/**
 * Bestätigung vor dem Aktivieren einer Vorlage aus einem CreatorOffice-Muster (AGB, Abschnitt „Leistungen“, 07.10.2026):
 * Muster sind unverbindliche Beispiele ohne Rechtsberatung; welche Verträge das Konto verwendet, verantwortet es selbst.
 */
export const musterVerantwortung = () => ta(
  "Ich habe dieses Muster geprüft, bei Bedarf rechtlich prüfen lassen und an unsere Agentur angepasst. Muster sind unverbindliche Beispiele und keine Rechtsberatung; welche Verträge wir verwenden, verantworten wir selbst.",
  "I have checked this sample, had it legally reviewed if necessary and adapted it to our agency. Samples are non-binding examples and not legal advice; we are responsible for the contracts we use.",
  "Ich habe dieses Muster geprüft, bei Bedarf rechtlich prüfen lassen und an mein Geschäft angepasst. Muster sind unverbindliche Beispiele und keine Rechtsberatung; welche Verträge ich verwende, verantworte ich selbst.",
  "I have checked this sample, had it legally reviewed if necessary and adapted it to my business. Samples are non-binding examples and not legal advice; I am responsible for the contracts I use.");
